import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {Save, RotateCcw, Check} from 'lucide-react'
import SEO from '@/components/SEO'
import {
  applyTokensToDocument,
  DEFAULT_TOKENS,
  loadTokens,
  saveTokens,
  StyleTokens,
} from '@/lib/styleTokens'

type Field =
  | { kind: 'color'; key: keyof StyleTokens; label: string }
  | { kind: 'number'; key: keyof StyleTokens; label: string; min?: number; max?: number; step?: number; unit?: string }
  | { kind: 'text'; key: keyof StyleTokens; label: string }

const SECTIONS: { title: string; description?: string; fields: Field[] }[] = [
  {
    title: 'Couleurs',
    description: 'Palette globale du site. Les changements s\'appliquent partout.',
    fields: [
      { kind: 'color', key: 'colorBg', label: 'Fond principal' },
      { kind: 'color', key: 'colorPaper', label: 'Fond secondaire (footer)' },
      { kind: 'color', key: 'colorBeige', label: 'Beige clair' },
      { kind: 'color', key: 'colorInk', label: 'Texte principal' },
      { kind: 'color', key: 'colorInkSoft', label: 'Texte secondaire' },
      { kind: 'color', key: 'colorPrimary', label: 'Accent primaire (bordeaux)' },
      { kind: 'color', key: 'colorPrimaryHover', label: 'Accent primaire — survol' },
      { kind: 'color', key: 'colorAccent', label: 'Accent doré (eyebrows)' },
      { kind: 'color', key: 'colorLine', label: 'Lignes / séparateurs' },
    ],
  },
  {
    title: 'Polices',
    description: 'Familles typographiques utilisées sur tout le site.',
    fields: [
      { kind: 'text', key: 'fontSerif', label: 'Police titres (serif)' },
      { kind: 'text', key: 'fontSans', label: 'Police corps (sans-serif)' },
    ],
  },
  {
    title: 'Titre H1 — Hero de page',
    fields: [
      { kind: 'number', key: 'h1Size', label: 'Taille desktop (px)', min: 24, max: 120, step: 1 },
      { kind: 'number', key: 'h1SizeMobile', label: 'Taille mobile (px)', min: 18, max: 80, step: 1 },
      { kind: 'number', key: 'h1Weight', label: 'Graisse', min: 100, max: 900, step: 100 },
      { kind: 'number', key: 'h1Tracking', label: 'Espacement lettres (em)', min: -0.1, max: 0.2, step: 0.005 },
    ],
  },
  {
    title: 'Titre H2 — Sections',
    fields: [
      { kind: 'number', key: 'h2Size', label: 'Taille desktop (px)', min: 16, max: 80, step: 1 },
      { kind: 'number', key: 'h2SizeMobile', label: 'Taille mobile (px)', min: 14, max: 60, step: 1 },
      { kind: 'number', key: 'h2Weight', label: 'Graisse', min: 100, max: 900, step: 100 },
    ],
  },
  {
    title: 'Titre H3 — Sous-sections',
    fields: [
      { kind: 'number', key: 'h3Size', label: 'Taille desktop (px)', min: 14, max: 60, step: 1 },
      { kind: 'number', key: 'h3SizeMobile', label: 'Taille mobile (px)', min: 12, max: 48, step: 1 },
      { kind: 'number', key: 'h3Weight', label: 'Graisse', min: 100, max: 900, step: 100 },
    ],
  },
  {
    title: 'Texte courant (body)',
    fields: [
      { kind: 'number', key: 'bodySize', label: 'Taille (px)', min: 12, max: 22, step: 1 },
      { kind: 'number', key: 'bodyWeight', label: 'Graisse', min: 100, max: 900, step: 100 },
      { kind: 'number', key: 'bodyLineHeight', label: 'Hauteur de ligne', min: 1, max: 2.4, step: 0.05 },
    ],
  },
  {
    title: 'Intro / sous-titre italique',
    fields: [
      { kind: 'number', key: 'introSize', label: 'Taille desktop (px)', min: 14, max: 36, step: 1 },
      { kind: 'number', key: 'introSizeMobile', label: 'Taille mobile (px)', min: 12, max: 28, step: 1 },
    ],
  },
  {
    title: 'Eyebrows (petits libellés dorés)',
    fields: [
      { kind: 'number', key: 'eyebrowSize', label: 'Taille (px)', min: 8, max: 16, step: 1 },
      { kind: 'number', key: 'eyebrowTracking', label: 'Espacement lettres (em)', min: 0, max: 0.6, step: 0.01 },
      { kind: 'number', key: 'eyebrowWeight', label: 'Graisse', min: 100, max: 900, step: 100 },
    ],
  },
  {
    title: 'Boutons',
    fields: [
      { kind: 'number', key: 'buttonFontSize', label: 'Taille texte (px)', min: 10, max: 20, step: 1 },
      { kind: 'number', key: 'buttonPaddingX', label: 'Padding horizontal (px)', min: 12, max: 80, step: 1 },
      { kind: 'number', key: 'buttonPaddingY', label: 'Padding vertical (px)', min: 6, max: 40, step: 1 },
      { kind: 'number', key: 'buttonRadius', label: 'Rayon des coins (px)', min: 0, max: 40, step: 1 },
      { kind: 'number', key: 'buttonTracking', label: 'Espacement lettres (em)', min: 0, max: 0.4, step: 0.01 },
    ],
  },
  {
    title: 'Mise en page',
    fields: [
      { kind: 'number', key: 'containerMaxWidth', label: 'Largeur max conteneur (px)', min: 800, max: 1800, step: 10 },
      { kind: 'number', key: 'sectionPaddingY', label: 'Padding vertical sections desktop (px)', min: 24, max: 200, step: 4 },
      { kind: 'number', key: 'sectionPaddingYMobile', label: 'Padding vertical sections mobile (px)', min: 16, max: 160, step: 4 },
    ],
  },
]

function AdminStyles() {
  const [tokens, setTokens] = useState<StyleTokens>(DEFAULT_TOKENS)
  const [recordId, setRecordId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedFlash, setSavedFlash] = useState(false)

  // Load from DB on mount
  useEffect(() => {
    loadTokens().then(({ tokens: loaded, recordId: id }) => {
      setTokens(loaded)
      setRecordId(id)
      applyTokensToDocument(loaded)
      setLoading(false)
    })
  }, [])

  // Apply live whenever tokens change
  useEffect(() => {
    if (!loading) applyTokensToDocument(tokens)
  }, [tokens, loading])

  const update = (key: keyof StyleTokens, value: string) => {
    setTokens(prev => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const id = await saveTokens(tokens, recordId)
      setRecordId(id)
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 2000)
    } catch (err) {
      console.error('Save failed:', err)
      alert('Échec de l\'enregistrement. Vérifiez la console.')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    if (!confirm('Réinitialiser tous les styles aux valeurs par défaut ?')) return
    setTokens(DEFAULT_TOKENS)
    applyTokensToDocument(DEFAULT_TOKENS)
  }

  const previewStyles = useMemo(
    () => ({
      titleSerif: { fontFamily: tokens.fontSerif },
      bodySans: { fontFamily: tokens.fontSans },
    }),
    [tokens.fontSerif, tokens.fontSans]
  )

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#1F1B1D]" style={previewStyles.bodySans}>
      <SEO
        title="Admin — HeartCanvas"
        description="Interface d'administration"
        path="/admin"
        noindex={true}
      />
      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E5E5E5]">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[12px] tracking-[0.2em] uppercase text-[#666] hover:text-[#1F1B1D]">
              ← Retour au site
            </Link>
            <div className="h-5 w-px bg-[#E5E5E5]" />
            <h1 className="text-[16px] font-medium">HeartCanvas — Feuille de style</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-2 px-4 py-2 text-[12px] tracking-wide border border-[#E5E5E5] hover:bg-[#F5F5F5] transition"
            >
              <RotateCcw size={14} /> Réinitialiser
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || loading}
              className="inline-flex items-center gap-2 px-5 py-2 text-[12px] tracking-wide bg-[#1F1B1D] text-white hover:bg-[#000] transition disabled:opacity-50"
            >
              {savedFlash ? <Check size={14} /> : <Save size={14} />}
              {saving ? 'Enregistrement…' : savedFlash ? 'Enregistré' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-8">
        {/* LEFT — controls */}
        <aside className="space-y-8">
          {loading && <div className="text-[13px] text-[#666]">Chargement…</div>}
          {!loading && SECTIONS.map((section) => (
            <section key={section.title} className="bg-white border border-[#E5E5E5] rounded-sm">
              <div className="px-5 py-4 border-b border-[#E5E5E5]">
                <h2 className="text-[14px] font-medium tracking-wide">{section.title}</h2>
                {section.description && (
                  <p className="text-[12px] text-[#666] mt-1">{section.description}</p>
                )}
              </div>
              <div className="px-5 py-4 space-y-4">
                {section.fields.map((field) => (
                  <FieldRow
                    key={String(field.key)}
                    field={field}
                    value={tokens[field.key]}
                    onChange={(v) => update(field.key, v)}
                  />
                ))}
              </div>
            </section>
          ))}
        </aside>

        {/* RIGHT — live preview */}
        <main className="space-y-6">
          <div className="bg-white border border-[#E5E5E5] rounded-sm">
            <div className="px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
              <h2 className="text-[12px] tracking-[0.2em] uppercase text-[#666]">Aperçu en direct</h2>
              <span className="text-[11px] text-[#999]">Les changements sont visibles partout sur le site</span>
            </div>
            <div
              className="p-10"
              style={{
                backgroundColor: tokens.colorBg,
                color: tokens.colorInk,
              }}
            >
              {/* Eyebrow */}
              <div
                className="uppercase mb-6"
                style={{
                  color: tokens.colorAccent,
                  fontSize: `${tokens.eyebrowSize}px`,
                  letterSpacing: `${tokens.eyebrowTracking}em`,
                  fontWeight: Number(tokens.eyebrowWeight),
                }}
              >
                — Aperçu eyebrow
              </div>

              {/* H1 */}
              <h1
                className="mb-4"
                style={{
                  fontFamily: tokens.fontSerif,
                  fontSize: `clamp(${tokens.h1SizeMobile}px, 5vw, ${tokens.h1Size}px)`,
                  fontWeight: Number(tokens.h1Weight),
                  letterSpacing: `${tokens.h1Tracking}em`,
                  lineHeight: 1.05,
                }}
              >
                Crée le canevas de ta vie.
              </h1>

              {/* Intro */}
              <p
                className="italic mb-8 max-w-2xl"
                style={{
                  fontFamily: tokens.fontSans,
                  fontSize: `clamp(${tokens.introSizeMobile}px, 2vw, ${tokens.introSize}px)`,
                  color: tokens.colorInkSoft,
                  lineHeight: Number(tokens.bodyLineHeight),
                }}
              >
                Un parcours pour te révéler à toi-même — par l'art.
              </p>

              {/* Buttons */}
              <div className="flex flex-wrap gap-4 mb-12">
                <button
                  type="button"
                  className="uppercase transition"
                  style={{
                    backgroundColor: tokens.colorPrimary,
                    color: tokens.colorBg,
                    padding: `${tokens.buttonPaddingY}px ${tokens.buttonPaddingX}px`,
                    fontSize: `${tokens.buttonFontSize}px`,
                    letterSpacing: `${tokens.buttonTracking}em`,
                    borderRadius: `${tokens.buttonRadius}px`,
                  }}
                  onMouseEnter={(e) => {
                    ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = tokens.colorPrimaryHover
                  }}
                  onMouseLeave={(e) => {
                    ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = tokens.colorPrimary
                  }}
                >
                  Bouton primaire
                </button>
                <button
                  type="button"
                  className="uppercase transition"
                  style={{
                    border: `1px solid ${tokens.colorAccent}`,
                    color: tokens.colorAccent,
                    backgroundColor: 'transparent',
                    padding: `${tokens.buttonPaddingY}px ${tokens.buttonPaddingX}px`,
                    fontSize: `${tokens.buttonFontSize}px`,
                    letterSpacing: `${tokens.buttonTracking}em`,
                    borderRadius: `${tokens.buttonRadius}px`,
                  }}
                >
                  Bouton secondaire
                </button>
              </div>

              {/* Separator */}
              <div className="h-px mb-12" style={{ backgroundColor: tokens.colorLine }} />

              {/* H2 */}
              <h2
                className="mb-6"
                style={{
                  fontFamily: tokens.fontSerif,
                  fontSize: `clamp(${tokens.h2SizeMobile}px, 4vw, ${tokens.h2Size}px)`,
                  fontWeight: Number(tokens.h2Weight),
                  lineHeight: 1.15,
                }}
              >
                Titre de section (H2)
              </h2>

              {/* Body paragraph */}
              <p
                className="mb-10 max-w-2xl"
                style={{
                  fontFamily: tokens.fontSans,
                  fontSize: `${tokens.bodySize}px`,
                  fontWeight: Number(tokens.bodyWeight),
                  lineHeight: Number(tokens.bodyLineHeight),
                  color: tokens.colorInkSoft,
                }}
              >
                L'art n'est pas une question de talent. C'est une expérience vivante où tu retrouveras
                de la fluidité, ta capacité à créer et à t'amuser — et une façon d'entrer en relation
                avec toi-même.
              </p>

              {/* H3 */}
              <h3
                className="mb-4"
                style={{
                  fontFamily: tokens.fontSerif,
                  fontSize: `clamp(${tokens.h3SizeMobile}px, 3vw, ${tokens.h3Size}px)`,
                  fontWeight: Number(tokens.h3Weight),
                  lineHeight: 1.2,
                }}
              >
                Sous-titre (H3)
              </h3>

              {/* List */}
              <ul
                className="space-y-3 mb-10 max-w-2xl"
                style={{
                  fontFamily: tokens.fontSans,
                  fontSize: `${tokens.bodySize}px`,
                  lineHeight: Number(tokens.bodyLineHeight),
                  color: tokens.colorInkSoft,
                }}
              >
                <li className="flex gap-3">
                  <span style={{ color: tokens.colorAccent }}>—</span>
                  Tu sens qu'il y a quelque chose en toi qui attend d'émerger.
                </li>
                <li className="flex gap-3">
                  <span style={{ color: tokens.colorAccent }}>—</span>
                  Tu cherches de la clarté — sur qui tu es, ce que tu veux.
                </li>
                <li className="flex gap-3">
                  <span style={{ color: tokens.colorAccent }}>—</span>
                  Tu portes quelque chose de lourd sans savoir comment le déposer.
                </li>
              </ul>

              {/* Link */}
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="inline-block underline-offset-4"
                style={{
                  fontFamily: tokens.fontSans,
                  color: tokens.colorPrimary,
                  fontSize: `${tokens.bodySize}px`,
                  textDecoration: 'underline',
                }}
              >
                Lien dans un texte
              </a>

              {/* Color swatches */}
              <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Fond', value: tokens.colorBg },
                  { label: 'Paper', value: tokens.colorPaper },
                  { label: 'Primary', value: tokens.colorPrimary },
                  { label: 'Accent', value: tokens.colorAccent },
                  { label: 'Ink', value: tokens.colorInk },
                  { label: 'Ink soft', value: tokens.colorInkSoft },
                  { label: 'Beige', value: tokens.colorBeige },
                  { label: 'Line', value: tokens.colorLine },
                ].map((c) => (
                  <div key={c.label} className="border border-[#E5E5E5]">
                    <div className="h-12" style={{ backgroundColor: c.value }} />
                    <div className="px-2 py-1 text-[11px] flex items-center justify-between bg-white">
                      <span className="text-[#666]">{c.label}</span>
                      <code className="text-[10px] text-[#999]">{c.value}</code>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-[12px] text-[#666] px-1">
            Les valeurs sont injectées en CSS variables sur l'ensemble du site. Cliquez sur
            <strong className="font-medium"> Enregistrer </strong> pour les sauvegarder définitivement.
          </div>
        </main>
      </div>
    </div>
  )
}

function FieldRow({
  field,
  value,
  onChange,
}: {
  field: Field
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex items-center gap-3">
      <label className="flex-1 text-[12px] text-[#444]">{field.label}</label>
      {field.kind === 'color' && (
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-8 h-8 rounded cursor-pointer border border-[#E5E5E5]"
          />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-24 px-2 py-1 text-[11px] font-mono border border-[#E5E5E5] focus:outline-none focus:border-[#1F1B1D]"
          />
        </div>
      )}
      {field.kind === 'number' && (
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={field.min}
            max={field.max}
            step={field.step ?? 1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-32 accent-[#1F1B1D]"
          />
          <input
            type="number"
            min={field.min}
            max={field.max}
            step={field.step ?? 1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-20 px-2 py-1 text-[12px] border border-[#E5E5E5] focus:outline-none focus:border-[#1F1B1D]"
          />
        </div>
      )}
      {field.kind === 'text' && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-56 px-2 py-1 text-[12px] border border-[#E5E5E5] focus:outline-none focus:border-[#1F1B1D]"
        />
      )}
    </div>
  )
}

export default AdminStyles
